import { useState, useCallback } from 'react'
import { mediaApi, type MediaFile } from '../utils/api'

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/avif', 'video/mp4', 'application/pdf']

type UploadState = {
  file: File
  progress: 'uploading' | 'done' | 'error'
  error?: string
}

type Toast = { type: 'success' | 'error'; message: string } | null

type Props = {
  files: MediaFile[]
  loading: boolean
  onRefresh: () => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function getFileIcon(key: string): string {
  const ext = key.split('.').pop()?.toLowerCase() ?? ''
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg'].includes(ext)) return '🖼️'
  if (['mp4', 'webm', 'mov'].includes(ext)) return '🎬'
  if (ext === 'pdf') return '📄'
  if (['json', 'js', 'ts'].includes(ext)) return '📋'
  return '📁'
}

export default function MediaManager({ files, loading, onRefresh }: Props) {
  const [dragging, setDragging] = useState(false)
  const [uploads, setUploads] = useState<UploadState[]>([])
  const [toast, setToast] = useState<Toast>(null)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [folder, setFolder] = useState('general')
  
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [editingFile, setEditingFile] = useState<MediaFile | null>(null)
  const [editForm, setEditForm] = useState({ newKey: '', altText: '' })
  const [savingMetadata, setSavingMetadata] = useState(false)

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message })
    setTimeout(() => setToast(null), 3500)
  }

  const uploadFiles = useCallback(async (fileList: FileList | File[]) => {
    const filesToUpload = Array.from(fileList).filter(
      (f) => ACCEPTED_TYPES.includes(f.type)
    )

    if (!filesToUpload.length) {
      showToast('error', 'No supported file types selected')
      return
    }

    // Add to upload queue
    setUploads((prev) => [
      ...prev,
      ...filesToUpload.map((f) => ({ file: f, progress: 'uploading' as const })),
    ])

    // Upload concurrently
    await Promise.all(
      filesToUpload.map(async (file) => {
        try {
          await mediaApi.upload(file, folder)
          setUploads((prev) =>
            prev.map((u) => u.file === file ? { ...u, progress: 'done' as const } : u)
          )
        } catch (err) {
          setUploads((prev) =>
            prev.map((u) =>
              u.file === file
                ? { ...u, progress: 'error' as const, error: err instanceof Error ? err.message : 'Upload failed' }
                : u
            )
          )
        }
      })
    )

    showToast('success', `✓ ${filesToUpload.length} file${filesToUpload.length > 1 ? 's' : ''} uploaded`)
    onRefresh()
    setTimeout(() => setUploads([]), 3000)
  }, [onRefresh])

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragging(true)
  }

  function handleDragLeave() {
    setDragging(false)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files.length) {
      uploadFiles(e.dataTransfer.files)
    }
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) {
      uploadFiles(e.target.files)
      e.target.value = ''
    }
  }

  async function copyUrl(file: MediaFile) {
    const fullUrl = `${import.meta.env.VITE_API_BASE ?? ''}${file.url}`
    await navigator.clipboard.writeText(fullUrl)
    setCopiedKey(file.key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  function handleEdit(file: MediaFile) {
    setEditingFile(file)
    setEditForm({
      newKey: file.key,
      altText: file.customMetadata?.alt || ''
    })
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingFile) return
    
    setSavingMetadata(true)
    try {
      await mediaApi.update({
        oldKey: editingFile.key,
        newKey: editForm.newKey,
        altText: editForm.altText
      })
      showToast('success', `✓ Saved changes to ${editForm.newKey}`)
      setEditingFile(null)
      onRefresh()
    } catch (err) {
      showToast('error', `Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setSavingMetadata(false)
    }
  }

  async function handleDelete(key: string) {
    if (!confirm(`Delete "${key}"? This cannot be undone.`)) return
    setDeleting(key)
    try {
      await mediaApi.delete(key)
      showToast('success', `✓ Deleted ${key}`)
      onRefresh()
    } catch (err) {
      showToast('error', `Failed to delete: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setDeleting(null)
    }
  }

  const isImage = (key: string) => /\.(jpg|jpeg|png|gif|webp|avif|svg)$/i.test(key)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── Upload Zone ─────────────────────────────────────────────────────── */}
      <div
        id="upload-drop-zone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 16,
          padding: '40px 24px',
          textAlign: 'center',
          background: dragging ? 'var(--accent-dim)' : 'var(--bg-glass-light)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          boxShadow: dragging ? 'var(--shadow-glow)' : 'none',
        }}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <div style={{ fontSize: '2.5rem', marginBottom: 12, opacity: 0.7 }}>
          {dragging ? '📂' : '⬆️'}
        </div>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
          {dragging ? 'Drop files here' : 'Drag & drop files here'}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 16 }}>
          or click to browse · JPG, PNG, GIF, WebP, SVG, AVIF, MP4, PDF · max 50 MB
        </div>

        <div style={{ marginBottom: 16 }}>
          <select 
            value={folder} 
            onChange={(e) => setFolder(e.target.value)}
            style={{
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-subtle)',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: '0.85rem',
              outline: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="root">Root (/) </option>
            <option value="general">General</option>
            <option value="blog">Blog</option>
            <option value="banners">Banners</option>
            <option value="avatars">Avatars</option>
          </select>
        </div>
        <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
          📁 Browse Files
          <input
            id="file-input"
            type="file"
            multiple
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {/* ── Upload Queue ─────────────────────────────────────────────────────── */}
      {uploads.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {uploads.map((u, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 14px',
              borderRadius: 8,
              background: u.progress === 'error' ? 'var(--danger-dim)' : u.progress === 'done' ? 'var(--success-dim)' : 'var(--bg-glass-light)',
              border: `1px solid ${u.progress === 'error' ? 'rgba(248,113,113,0.25)' : u.progress === 'done' ? 'rgba(74,222,128,0.25)' : 'var(--border)'}`,
              fontSize: '0.8rem',
            }}>
              <span>
                {u.progress === 'uploading' && <span className="animate-spin" style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%' }} />}
                {u.progress === 'done' && '✓'}
                {u.progress === 'error' && '✗'}
              </span>
              <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.file.name}</span>
              <span style={{ color: 'var(--text-muted)' }}>{formatBytes(u.file.size)}</span>
              {u.error && <span style={{ color: 'var(--danger)' }}>{u.error}</span>}
            </div>
          ))}
        </div>
      )}

      {/* ── Asset Grid ──────────────────────────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Assets
            {!loading && <span style={{ fontWeight: 400, marginLeft: 8, color: 'var(--text-muted)', opacity: 0.7 }}>({files.length})</span>}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onRefresh} disabled={loading} id="btn-refresh-media">
            {loading ? '⟳' : '↻'} Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 160, borderRadius: 10 }} />
            ))}
          </div>
        ) : files.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🖼️</div>
            <p>No files uploaded yet</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 12,
          }}>
            {files.map((file) => (
              <div
                key={file.key}
                className="glass-card-sm"
                style={{
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent-border)'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-glow)'
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-subtle)'
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
                }}
              >
                <div 
                  onClick={() => isImage(file.key) && setPreviewImage(`${import.meta.env.VITE_API_BASE ?? ''}${file.url}`)}
                  style={{
                    height: 110,
                    background: 'var(--bg-elevated)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    borderBottom: '1px solid var(--border-subtle)',
                    cursor: isImage(file.key) ? 'zoom-in' : 'default'
                  }}
                >
                  {isImage(file.key) ? (
                    <img
                      src={`${import.meta.env.VITE_API_BASE ?? ''}${file.url}`}
                      alt={file.customMetadata?.alt || file.key}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      loading="lazy"
                      onError={(e) => { 
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%231a2e2a"/><text x="50" y="55" font-size="40" text-anchor="middle">❌</text></svg>' 
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: '2.5rem', opacity: 0.5 }}>{getFileIcon(file.key)}</span>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding: '10px 10px 8px' }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'var(--text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: 4,
                    fontFamily: 'var(--font-mono)',
                  }} title={file.key}>
                    {file.key}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {formatBytes(file.size)}
                    {file.uploaded && ` · ${new Date(file.uploaded).toLocaleDateString()}`}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button
                      id={`btn-copy-md-${file.key.replace(/[^a-z0-9]/gi, '-')}`}
                      className="btn btn-ghost btn-sm"
                      style={{ flex: 1, fontSize: '0.72rem', padding: '4px 8px', justifyContent: 'center' }}
                      onClick={() => copyUrl(file)}
                      title="Copy public URL"
                    >
                      {copiedKey === file.key ? '✓ Copied' : '🔗 URL'}
                    </button>
                    <button
                      id={`btn-edit-${file.key.replace(/[^a-z0-9]/gi, '-')}`}
                      className="btn btn-ghost btn-sm"
                      style={{ padding: '4px 8px', fontSize: '0.72rem' }}
                      onClick={() => handleEdit(file)}
                      title="Edit metadata"
                    >
                      ✏️
                    </button>
                    <button
                      id={`btn-delete-${file.key.replace(/[^a-z0-9]/gi, '-')}`}
                      className="btn btn-danger btn-sm"
                      style={{ padding: '4px 8px', fontSize: '0.72rem' }}
                      onClick={() => handleDelete(file.key)}
                      disabled={deleting === file.key}
                      title="Delete file from R2"
                    >
                      {deleting === file.key ? '…' : '🗑'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
          {toast.message}
        </div>
      )}

      {/* ── Lightbox Modal ─────────────────────────────────────────────────── */}
      {previewImage && (
        <div 
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out', padding: 40
          }}
          onClick={() => setPreviewImage(null)}
        >
          <img 
            src={previewImage} 
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }} 
            alt="Preview" 
          />
        </div>
      )}

      {/* ── Edit Metadata Modal ────────────────────────────────────────────── */}
      {editingFile && (
        <div 
          style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(9, 20, 19, 0.8)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
          }}
          onClick={() => setEditingFile(null)}
        >
          <div 
            className="glass-panel"
            style={{ width: '100%', maxWidth: 500, padding: 24 }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '1.2rem', marginBottom: 16 }}>Edit Media</h2>
            
            <form onSubmit={saveEdit}>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 6, color: 'var(--text-secondary)' }}>File Path (Key)</label>
                <input 
                  type="text" 
                  value={editForm.newKey}
                  onChange={e => setEditForm({ ...editForm, newKey: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}
                  required
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Note: Changing this will copy the file to the new path and delete the old one.</div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 6, color: 'var(--text-secondary)' }}>Alt Text (SEO)</label>
                <input 
                  type="text" 
                  value={editForm.altText}
                  onChange={e => setEditForm({ ...editForm, altText: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)' }}
                  placeholder="Describe this image..."
                />
              </div>

              <div style={{ display: 'flex', gap: 16, marginBottom: 24, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                <div><strong>Size:</strong> {formatBytes(editingFile.size)}</div>
                <div><strong>Uploaded:</strong> {editingFile.uploaded ? new Date(editingFile.uploaded).toLocaleDateString() : 'Unknown'}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditingFile(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingMetadata}>
                  {savingMetadata ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
